import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import Icon from "@mdi/react";
import { mdiAlertCircle, mdiLockOutline, mdiClockOutline } from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import { bookingApi } from "../../api/booking";
import { paymentApi } from "../../api/payment";
import SupplierPolicyAcknowledgement, { policyToSections } from "../../components/SupplierPolicyAcknowledgement";
import { formatCurrencyFromMinor as formatCurrencyValue } from "../../utils/currency";
import { isOrgPaymentsNotReady, orgPaymentsNotReadyMessage } from "../../utils/orgPayments";
import "../../style.css";
import { getStripePromise } from "../../utils/stripeKey";
import ServiceFeeInfo from "../../components/ServiceFeeInfo";

// Preserve the pre-migration empty-state behaviour: missing amount renders "—",
// a real 0 still formats as A$0.00.
const formatCurrency = (amount, currency) =>
    amount == null ? "—" : formatCurrencyValue(amount, currency);


/**
 * PaymentForm - Stripe Elements payment form.
 * Rendered inside an Elements provider with the PaymentIntent clientSecret.
 */
function PaymentForm({ bookingPreview, privateLessonPolicy, supplierName, onSuccess, onError }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState("");
    const [policyAck, setPolicyAck] = useState(false);
    const [elementsReady, setElementsReady] = useState(false);

    const requireAck = !!privateLessonPolicy;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !elementsReady) return;
        if (requireAck && !policyAck) return;

        setIsProcessing(true);
        setPaymentError("");

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
            });

            if (error) {
                setPaymentError(error.message || "Payment failed. Please try again.");
                setIsProcessing(false);
                onError(error.message);
                return;
            }

            if (paymentIntent && paymentIntent.status === "succeeded") {
                onSuccess(paymentIntent.id);
            } else {
                setPaymentError("Payment not completed. Please try again.");
                setIsProcessing(false);
            }
        } catch (err) {
            setPaymentError(err.message || "Payment failed. Please try again.");
            setIsProcessing(false);
            onError(err.message);
        }
    };

    const pricing = bookingPreview?.pricing;

    return (
        <form onSubmit={handleSubmit} style={styles.form}>
            {/* Booking Summary */}
            <div style={styles.summaryCard}>
                <h3 style={styles.summaryTitle}>Lesson Payment</h3>
                {bookingPreview && (
                    <>
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Instructor</span>
                            <span style={styles.summaryValue}>{bookingPreview.instructorName}</span>
                        </div>
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Date</span>
                            <span style={styles.summaryValue}>{bookingPreview.dateDisplay}</span>
                        </div>
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Time</span>
                            <span style={styles.summaryValue}>
                                {bookingPreview.timeSlot?.startTime} – {bookingPreview.timeSlot?.endTime}
                            </span>
                        </div>
                        {pricing?.artistFeeSubtotal > 0 && (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Artist fee</span>
                                <span style={styles.summaryValue}>
                                    {formatCurrency(pricing.artistFeeSubtotal, pricing.currency)}
                                </span>
                            </div>
                        )}
                        {pricing?.venueHireSubtotal > 0 && (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Venue hire</span>
                                <span style={styles.summaryValue}>
                                    {formatCurrency(pricing.venueHireSubtotal, pricing.currency)}
                                </span>
                            </div>
                        )}
                        {/* Service fee = platform + processing (one combined line), so the summary
                            reconciles to pricing.amount. A reservation preview has no
                            effectivePricing (it isn't a booking yet), so sum the server's snapshot. */}
                        {(pricing?.platformFeeSubtotal > 0 || pricing?.processingFeeSubtotal > 0) && (
                            <div style={styles.summaryRow}>
                                <ServiceFeeInfo labelStyle={styles.summaryLabel} />
                                <span style={styles.summaryValue}>
                                    {formatCurrency(
                                        (pricing.platformFeeSubtotal || 0) + (pricing.processingFeeSubtotal || 0),
                                        pricing.currency
                                    )}
                                </span>
                            </div>
                        )}
                        <div style={{ ...styles.summaryRow, ...styles.totalRow }}>
                            <span style={styles.totalLabel}>Total</span>
                            <span style={styles.totalValue}>
                                {formatCurrency(pricing?.amount, pricing?.currency)}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {requireAck && (
                <SupplierPolicyAcknowledgement
                    sections={policyToSections(privateLessonPolicy)}
                    supplierName={supplierName || bookingPreview?.instructorName}
                    surfaceLabel="lesson"
                    acknowledged={policyAck}
                    onAcknowledgedChange={setPolicyAck}
                />
            )}

            {/* Stripe Payment Element */}

            <div style={styles.paymentElementWrapper}>
                <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => setPaymentError(e?.error?.message || "Could not load the payment form. Please try again.")} options={{ layout: "tabs", paymentMethodOrder: ["apple_pay", "google_pay", "card"] }} />
            </div>

            {paymentError && (
                <div style={styles.errorBanner}>{paymentError}</div>
            )}

            <button
                type="submit"
                disabled={!stripe || !elements || !elementsReady || isProcessing || (requireAck && !policyAck)}
                style={{
                    ...styles.payButton,
                    opacity: (!stripe || !elements || !elementsReady || isProcessing || (requireAck && !policyAck)) ? 0.6 : 1,
                    cursor: (!stripe || !elements || !elementsReady || isProcessing || (requireAck && !policyAck)) ? "not-allowed" : "pointer",
                }}
            >
                {isProcessing
                    ? "Processing…"
                    : `Pay ${formatCurrency(pricing?.amount, pricing?.currency)}`}
            </button>

            <p style={styles.secureNote}>
                <Icon path={mdiLockOutline} size={0.55} style={{ verticalAlign: "middle", marginRight: "4px" }} />
                Secured by Stripe. Your payment details are never stored on our servers.
            </p>
        </form>
    );
}

/**
 * GuestBookingPayment — Guest Stripe payment page.
 *
 * Two modes (detected from location.state):
 *   Guest mode:         { reservationId, expiresAt, bookingPreview } — no auth required
 *   Authenticated mode: { bookingId, bookingPreview } — uses authenticated payment endpoint
 *
 * Flow (guest):
 *   1. Create payment session via POST /api/v2/payment/create-guest
 *   2. Show Stripe Elements form with countdown timer
 *   3. On payment success → POST /api/v2/booking/from-reservation → /booking-success
 *
 * Flow (authenticated + before-lesson):
 *   1. Create payment session via POST /api/v2/payment/create
 *   2. Show Stripe Elements form
 *   3. On payment success → /booking-success
 */
const GuestBookingPayment = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [status, setStatus] = useState("initializing");
    const [error, setError] = useState("");
    const [clientSecret, setClientSecret] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const [branding, setBranding] = useState({});
    const [orgName, setOrgName] = useState('');
    const [policy, setPolicy] = useState(null);

    const initRef = useRef(false);

    const state = location.state || {};
    const { reservationId, expiresAt, bookingPreview, bookingId } = state;
    const isGuestMode = !!reservationId;

    // Countdown timer for reservation expiry
    useEffect(() => {
        if (!expiresAt) return;
        const interval = setInterval(() => {
            const remaining = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
            setSecondsLeft(remaining);
            if (remaining === 0) setStatus("expired");
        }, 1000);
        return () => clearInterval(interval);
    }, [expiresAt]);

    // Initialise Stripe PaymentIntent on mount
    useEffect(() => {
        if (initRef.current) return;
        if (!bookingPreview) {
            setError("No booking data found. Please go back and try again.");
            setStatus("error");
            return;
        }

        const init = async () => {
            initRef.current = true;
            try {
                const pricing = bookingPreview.pricing || {};
                const artistFeeSubtotal = pricing.artistFeeSubtotal || 0;
                const venueHireSubtotal = pricing.venueHireSubtotal || 0;

                let paymentResponse;

                try {
                    if (isGuestMode) {
                        // Guest: unauthenticated payment endpoint
                        paymentResponse = await paymentApi.createGuestPaymentIntent({
                            sourceType: "booking-reservation",
                            sourceId: reservationId,
                            guestEmail: bookingPreview.studentInfo?.email,
                            organisationId: bookingPreview.instructorId,
                            artistFeeSubtotal,
                            venueHireSubtotal,
                            currency: pricing.currency || "AUD",
                        });
                    } else {
                        // Authenticated: standard payment endpoint
                        paymentResponse = await paymentApi.createPaymentIntent({
                            sourceType: "booking",
                            sourceId: bookingId,
                            organisationId: bookingPreview.instructorId,
                            artistFeeSubtotal,
                            venueHireSubtotal,
                            currency: pricing.currency || "AUD",
                        });
                    }
                } catch (err) {
                    setError(isOrgPaymentsNotReady(err)
                        ? orgPaymentsNotReadyMessage(bookingPreview.instructorName, "your instructor")
                        : (err.message || "Failed to initialise payment. Please try again."));
                    setStatus("error");
                    return;
                }

                // Fetch org branding (graceful — payment page renders even if this fails)
                const { orgName: fetchedOrgName, branding: fetchedBranding, privateLessonPolicy: fetchedPolicy } =
                    await paymentApi.getOrgBrandingPublic(bookingPreview.instructorId);
                setBranding(fetchedBranding);
                setOrgName(fetchedOrgName || bookingPreview.instructorName || '');
                setPolicy(fetchedPolicy);

                setClientSecret(paymentResponse.paymentIntent.clientSecret);
                setStatus("paying");
            } catch (err) {
                console.error("GuestBookingPayment init error:", err);
                setError("An unexpected error occurred. Please try again.");
                setStatus("error");
            }
        };

        init();
    }, [reservationId, bookingId, bookingPreview, isGuestMode, user]);

    // Apply org branding CSS vars to page root after render
    useEffect(() => {
        const root = document.getElementById('guest-booking-payment-root');
        if (!root || !branding) return;
        if (branding.buttonColor)     root.style.setProperty('--school-btn-color', branding.buttonColor);
        if (branding.buttonTextColor) root.style.setProperty('--school-btn-text', branding.buttonTextColor);
        if (branding.headerColor)     root.style.setProperty('--school-header-color', branding.headerColor);
        if (branding.headerTextColor) root.style.setProperty('--school-header-text', branding.headerTextColor);
    }, [branding]);

    const handlePaymentSuccess = async () => {
        try {
            setStatus("confirming");

            if (isGuestMode) {
                // Guest: confirm booking from reservation
                let booking;
                try {
                    booking = await bookingApi.confirmFromReservation(reservationId);
                } catch (err) {
                    setError(err.message || "Payment received but booking confirmation failed. Contact support.");
                    setStatus("error");
                    return;
                }
                // Booking persisted — clear the persisted guest form state from
                // BookingConfirmation so it's not stale on the next session.
                try { sessionStorage.removeItem('bookingConfirmation.guestForm.v1'); } catch { /* ignore */ }
                navigate("/booking-success", {
                    state: {
                        bookingId: booking.id,
                        instructorName: bookingPreview.instructorName,
                        dateDisplay: bookingPreview.dateDisplay,
                        timeSlot: bookingPreview.timeSlot,
                        totalPrice: bookingPreview.pricing?.amount,
                        currency: bookingPreview.pricing?.currency,
                        // Prefills the create-account card's register link. Guest
                        // branch only — the authed branch never shows that card.
                        guestEmail: bookingPreview.studentInfo?.email || "",
                        paymentConfirmed: true,
                    },
                });
            } else {
                // Authenticated: record payment and confirm booking
                try {
                    await bookingApi.recordPayment(bookingId);
                } catch (err) {
                    setError(err.message || "Payment received but booking update failed. Contact support.");
                    setStatus("error");
                    return;
                }

                navigate("/booking-success", {
                    state: {
                        bookingId,
                        instructorName: bookingPreview.instructorName,
                        dateDisplay: bookingPreview.dateDisplay,
                        timeSlot: bookingPreview.timeSlot,
                        totalPrice: bookingPreview.pricing?.amount,
                        currency: bookingPreview.pricing?.currency,
                        paymentConfirmed: true,
                    },
                });
            }
        } catch (err) {
            console.error("GuestBookingPayment confirm error:", err);
            setError("Payment succeeded but booking confirmation failed. Please contact support.");
            setStatus("error");
        }
    };

    const handlePaymentError = (message) => {
        console.error("Payment error:", message);
    };

    const formatCountdown = (seconds) => {
        if (seconds === null) return "";
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    if (status === "initializing") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.spinner} />
                    <h2 style={styles.loadingTitle}>Setting up payment…</h2>
                    <p style={styles.loadingMessage}>Preparing your secure payment session</p>
                </div>
            </div>
        );
    }

    if (status === "confirming") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.spinner} />
                    <h2 style={styles.loadingTitle}>Confirming your booking…</h2>
                    <p style={styles.loadingMessage}>Your payment was received. Confirming your lesson now.</p>
                </div>
            </div>
        );
    }

    if (status === "expired") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.errorIcon}>
                        <Icon path={mdiAlertCircle} size={2.5} color="#fff" />
                    </div>
                    <h2 style={styles.errorTitle}>Reservation Expired</h2>
                    <p style={styles.loadingMessage}>
                        Your 10-minute reservation has expired. Please go back and select your time again.
                    </p>
                    <div style={styles.buttonRow}>
                        <button onClick={() => navigate(-2)} style={styles.returnButton}>
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.errorIcon}>
                        <Icon path={mdiAlertCircle} size={2.5} color="#fff" />
                    </div>
                    <h2 style={styles.errorTitle}>Payment Error</h2>
                    <p style={styles.loadingMessage}>{error}</p>
                    <div style={styles.buttonRow}>
                        <button onClick={() => navigate(-1)} style={styles.returnButton}>
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const stripeColorPrimary = branding?.buttonColor || branding?.headerColor || "#451f78";
    const studentName = bookingPreview?.studentInfo?.firstName && bookingPreview?.studentInfo?.lastName
        ? `${bookingPreview.studentInfo.firstName} ${bookingPreview.studentInfo.lastName}`
        : null;

    return (
        <div style={styles.paymentPage} id="guest-booking-payment-root">
            {/* School-branded header card */}
            <div style={styles.paymentHeaderCard}>
                <h1 style={styles.paymentHeaderTitle}>{orgName || "Lesson Payment"}</h1>
                <p style={styles.paymentHeaderSubtitle}>Lesson Payment</p>
                {studentName && (
                    <p style={styles.paymentHeaderStudentName}>For {studentName}</p>
                )}
            </div>

            {/* White content card */}
            <div style={styles.paymentBody}>
                {/* Countdown timer (guest mode only) */}
                {isGuestMode && secondsLeft !== null && secondsLeft > 0 && (
                    <div style={styles.countdownBanner}>
                        <Icon path={mdiClockOutline} size={0.7} style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        Slot reserved for{" "}
                        <strong style={{ color: secondsLeft < 60 ? "var(--dl-color-warning-red)" : "var(--dl-color-purple-default)" }}>
                            {formatCountdown(secondsLeft)}
                        </strong>
                    </div>
                )}

                {clientSecret && (
                    <Elements
                        stripe={getStripePromise()}
                        options={{
                            clientSecret,
                            appearance: {
                                theme: "stripe",
                                variables: {
                                    colorPrimary: stripeColorPrimary,
                                    colorBackground: "#ffffff",
                                    colorText: "#1a1a1a",
                                    borderRadius: "8px",
                                    fontFamily: "system-ui, sans-serif",
                                },
                            },
                        }}
                    >
                        <PaymentForm
                            bookingPreview={bookingPreview}
                            privateLessonPolicy={policy}
                            supplierName={orgName || bookingPreview?.instructorName}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                        />
                    </Elements>
                )}

                <div style={{ textAlign: 'right', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--dl-color-border)' }}>
                    <button onClick={() => navigate(-1)} style={styles.cancelLink}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    // Loading/error/expired/confirming states — full-screen gradient
    fullPage: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, var(--dl-color-purple-default) 0%, var(--dl-color-turquoise-default) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        fontFamily: "system-ui, -apple-system, sans-serif",
    },
    centeredBox: {
        textAlign: "center",
        color: "var(--dl-color-gray-white)",
        maxWidth: "400px",
        width: "100%",
    },
    spinner: {
        border: "3px solid rgba(255,255,255,0.3)",
        borderRadius: "50%",
        borderTop: "3px solid #fff",
        width: "40px",
        height: "40px",
        animation: "spin 1s linear infinite",
        margin: "0 auto 24px",
    },
    loadingTitle: {
        fontSize: "1.4rem",
        fontWeight: 600,
        marginBottom: "12px",
        color: "var(--dl-color-gray-white)",
    },
    loadingMessage: {
        fontSize: "1rem",
        opacity: 0.9,
        lineHeight: 1.5,
        color: "var(--dl-color-gray-white)",
    },
    errorIcon: {
        marginBottom: "16px",
    },
    errorTitle: {
        fontSize: "1.4rem",
        fontWeight: 600,
        marginBottom: "12px",
        color: "var(--dl-color-gray-white)",
    },
    buttonRow: {
        display: "flex",
        gap: "12px",
        justifyContent: "center",
        flexWrap: "wrap",
        marginTop: "24px",
    },
    returnButton: {
        padding: "12px 24px",
        background: "rgba(255,255,255,0.2)",
        border: "2px solid rgba(255,255,255,0.5)",
        color: "var(--dl-color-gray-white)",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.9rem",
        fontWeight: 500,
    },
    // Card layout styles for paying state
    paymentPage: {
        minHeight: "100vh",
        background: "var(--dl-color-gray-200)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 16px",
        fontFamily: "system-ui, -apple-system, sans-serif",
    },
    paymentHeaderCard: {
        width: "100%",
        maxWidth: "480px",
        background: "var(--school-header-color, var(--dl-color-purple-default))",
        color: "var(--school-header-text, var(--dl-color-gray-white))",
        borderRadius: "12px 12px 0 0",
        padding: "24px",
        textAlign: "center",
    },
    paymentHeaderTitle: {
        fontSize: "1.6rem",
        fontWeight: 700,
        margin: "0 0 6px",
    },
    paymentHeaderSubtitle: {
        fontSize: "1.1rem",
        fontWeight: 500,
        opacity: 0.95,
        margin: 0,
    },
    paymentHeaderStudentName: {
        fontSize: "0.95rem",
        opacity: 0.85,
        fontStyle: "italic",
        margin: "6px 0 0",
    },
    paymentBody: {
        background: "var(--dl-color-gray-white)",
        borderRadius: "0 0 12px 12px",
        padding: "24px",
        maxWidth: "480px",
        width: "100%",
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        boxSizing: "border-box",
    },
    countdownBanner: {
        background: "var(--dl-color-purple-50)",
        border: "1px solid var(--dl-color-purple-200)",
        borderRadius: "8px",
        padding: "10px 14px",
        fontSize: "0.875rem",
        color: "var(--dl-color-purple-default)",
        textAlign: "center",
        marginBottom: "16px",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    summaryCard: {
        background: "var(--dl-color-purple-50)",
        borderRadius: "10px",
        padding: "16px",
        border: "1px solid var(--dl-color-purple-200)",
    },
    summaryTitle: {
        fontSize: "1.2rem",
        fontWeight: 700,
        color: "var(--dl-color-purple-default)",
        margin: "0 0 14px",
    },
    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
    },
    summaryLabel: {
        fontSize: "0.875rem",
        color: "var(--dl-color-gray-600)",
    },
    summaryValue: {
        fontSize: "0.875rem",
        color: "var(--dl-color-text-primary)",
        fontWeight: 500,
        textAlign: "right",
        maxWidth: "60%",
    },
    totalRow: {
        borderTop: "1px solid var(--dl-color-purple-200)",
        paddingTop: "10px",
        marginTop: "6px",
        marginBottom: 0,
    },
    totalLabel: {
        fontSize: "1rem",
        fontWeight: 700,
        color: "var(--dl-color-text-primary)",
    },
    totalValue: {
        fontSize: "1.1rem",
        fontWeight: 700,
        color: "var(--dl-color-purple-default)",
    },
    walletHint: {
        fontSize: "0.78rem",
        color: "var(--dl-color-gray-500)",
        textAlign: "center",
        margin: "0 0 12px",
        padding: "8px 0",
        borderBottom: "1px solid var(--dl-color-gray-200)",
    },
    paymentElementWrapper: {
        padding: "4px 0",
    },
    errorBanner: {
        background: "var(--dl-color-gray-50)",
        border: "1px solid #ffcdd2",
        color: "var(--dl-color-warning-red)",
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: "0.875rem",
    },
    payButton: {
        background: "var(--school-btn-color, var(--dl-color-purple-default))",
        color: "var(--school-btn-text, var(--dl-color-gray-white))",
        border: "none",
        borderRadius: "10px",
        padding: "16px",
        fontSize: "1rem",
        fontWeight: 600,
        width: "100%",
        transition: "opacity 0.2s ease",
    },
    secureNote: {
        textAlign: "center",
        fontSize: "0.75rem",
        color: "var(--dl-color-gray-400)",
        margin: 0,
    },
    cancelLink: {
        background: "none",
        border: "none",
        color: "var(--dl-color-gray-400)",
        fontSize: "0.85rem",
        cursor: "pointer",
        padding: "4px 0",
    },
};

// Inject spinner keyframe once
if (typeof document !== "undefined" && !document.getElementById("guest-payment-keyframes")) {
    const style = document.createElement("style");
    style.id = "guest-payment-keyframes";
    style.textContent = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
}

export default GuestBookingPayment;
